import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocItem } from "@/components/DocItem";
import DocViewer from "@/components/DocViewer";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { authedUrl } from "@/lib/api";
import { errMsg } from "@/pages/Drivers";

// License photo, medical card, employment paperwork… stored per driver.
export const DriverDocs = ({ driver, api, getToken, onChanged }) => {
  const ref = useRef(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const docs = driver.documents || [];

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("label", label);
      try { await api.post(`/admin/drivers/${driver.user_id}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    if (ok) { toast.success(`${ok} document${ok === 1 ? "" : "s"} uploaded`); setLabel(""); onChanged(); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/driver-docs/${deleting.id}`); toast.success("Document deleted"); setDeleting(null); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const download = async (d) => window.open(await authedUrl(`/driver-docs/${d.id}/file?inline=0`, getToken), "_blank");

  return (
    <div className="space-y-4" data-testid="driver-docs">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="What is it? e.g. Medical card (optional)" className="h-11 sm:max-w-xs" data-testid="driver-doc-label-input" />
        <div role="button" tabIndex={0} onClick={() => !busy && ref.current?.click()} onKeyDown={(e) => { if (e.key === "Enter") ref.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) upload([...e.dataTransfer.files]); }}
          className={`flex-1 rounded-xl border-2 border-dashed px-4 py-3 text-sm text-center cursor-pointer transition-colors ${over ? "border-[#002FA7] bg-blue-50" : "border-slate-300 hover:border-[#002FA7]"}`} data-testid="driver-doc-dropzone">
          <input ref={ref} type="file" multiple accept="application/pdf,image/*,.doc,.docx" className="hidden" onChange={(e) => { const fs = [...(e.target.files || [])]; e.target.value = ""; upload(fs); }} data-testid="driver-doc-file-input" />
          {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span> : <span className="inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Drop files or click to upload</span>}
        </div>
      </div>
      {docs.length === 0 ? <p className="text-sm text-slate-500" data-testid="driver-docs-empty">No documents yet.</p> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-1">
              <div className="flex-1 min-w-0">
                <DocItem doc={d} onOpen={(x) => setViewer({ kind: "driver-doc", id: x.id, filename: x.original_filename, content_type: x.content_type })} onDelete={setDeleting}
                  extra={d.label ? <span className="pill bg-blue-50 text-[#002FA7] border border-blue-200" data-testid={`driver-doc-label-${d.id}`}>{d.label}</span> : null} />
              </div>
              <Button variant="ghost" size="icon" onClick={() => download(d)} aria-label={`Download ${d.original_filename}`} data-testid={`driver-doc-download-${d.id}`}><Download className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <DocViewer open={!!viewer} onClose={() => setViewer(null)} item={viewer} />
      <ConfirmDialog open={!!deleting} onCancel={() => setDeleting(null)} title="Delete this document?" description={`${deleting?.original_filename} will be removed from this driver's profile. This can't be undone.`} confirmLabel="Delete" onConfirm={remove} testId="driver-doc-delete-dialog" />
    </div>
  );
};
